import unescapedText from 'I18n/utils/unescapedText';
import { AccordionTransition } from 'foundations-components/transitional/components/AccordionTransition';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { Clickable } from 'foundations-components/transitional/components/Clickable';
import { RadioInput } from 'foundations-components/transitional/components/RadioInput';
import { syntheticEvent } from 'foundations-components/transitional/utils/SyntheticEvent';
import { Fragment, useEffect, useState } from 'react';
import { DownIcon } from 'foundations-assets/icons/components/DownIcon';
import { UpIcon } from 'foundations-assets/icons/components/UpIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
import { jsxs as _jsxs } from "react/jsx-runtime";
function OptionGroup(props) {
  const {
    forceOpen,
    LabelComponent,
    onGroupToggle,
    option,
    value
  } = props;
  const someChecked = option.options.some(nestedOption => value.includes(nestedOption.value));
  const allChecked = option.options.every(nestedOption => value.includes(nestedOption.value));
  const [open, setOpen] = useState(someChecked || !!forceOpen);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (forceOpen) setOpen(forceOpen);
  }, [forceOpen]);
  const handleGroupClick = () => setOpen(!open);
  const handleGroupChange = event => {
    if (event.source.target.checked) setOpen(true);
    onGroupToggle(event);
  };
  const handleLabelClick = e => {
    e.preventDefault();
    setOpen(!open);
  };
  const GroupLabelComponent = () => /*#__PURE__*/_jsx(Clickable, {
    onClick: handleLabelClick,
    children: LabelComponent ? /*#__PURE__*/_jsx(LabelComponent, {
      option: option
    }) : option.text
  });
  const GroupItemComponent = () => option.selectAll ? /*#__PURE__*/_jsx(OptionItem, Object.assign({}, props, {
    checked: allChecked,
    LabelComponent: GroupLabelComponent,
    indeterminate: someChecked,
    onChange: handleGroupChange
  })) : /*#__PURE__*/_jsx(Text, {
    children: option.text
  });
  if (option.flatLayout) {
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(GroupItemComponent, {}), option.options.map(nestedOption => /*#__PURE__*/_createElement(OptionItem, Object.assign({}, props, {
        checked: value.includes(nestedOption.value),
        key: nestedOption.value,
        nested: true,
        option: nestedOption
      })))]
    });
  }
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(Clickable, {
      "aria-expanded": open,
      onClick: handleGroupClick,
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        gap: "400",
        justifyContent: "space-between",
        marginBlockStart: "200",
        children: [/*#__PURE__*/_jsx(GroupItemComponent, {}), open ? /*#__PURE__*/_jsx(UpIcon, {}) : /*#__PURE__*/_jsx(DownIcon, {})]
      })
    }), /*#__PURE__*/_jsx(AccordionTransition, {
      open: open,
      children: option.options.map(nestedOption => /*#__PURE__*/_createElement(OptionItem, Object.assign({}, props, {
        checked: value.includes(nestedOption.value),
        key: nestedOption.value,
        nested: true,
        option: nestedOption
      })))
    })]
  });
}
function OptionItem({
  checked,
  disabled,
  InputComponent,
  indeterminate,
  LabelComponent,
  name,
  nested,
  onChange,
  option
}) {
  const label = LabelComponent ? /*#__PURE__*/_jsx(LabelComponent, {
    option: option
  }) : option.text;
  const handleChange = event => onChange(syntheticEvent(option, event));
  const value = 'value' in option ? option.value : undefined;
  const ariaLabel = checked ? unescapedText('marketplace-ui-common.compatibility.planCompatibilityFilter.ariaLabelSelected', {
    option: option.text
  }) : unescapedText('marketplace-ui-common.compatibility.planCompatibilityFilter.ariaLabelSelect', {
    option: option.text
  });
  return /*#__PURE__*/_jsx(InputComponent, {
    "aria-label": ariaLabel,
    checked: checked,
    "data-checked": checked,
    "data-test-id": `filter-toggle-input::${name}::${value}`,
    disabled: disabled,
    indeterminate: indeterminate,
    marginInlineStart: nested ? '300' : '0',
    name: name,
    onChange: handleChange,
    onClick: e => e.stopPropagation(),
    size: "small",
    value: value,
    children: label
  });
}
export default function FilterToggle(props) {
  const {
    multi,
    onChange,
    option
  } = props;
  const InputComponent = multi ? Checkbox : RadioInput;
  const handleChange = event => {
    const checked = event.source.target.checked;
    const eventValue = event.target.value.value;
    if (multi) {
      const _value = props.value === undefined ? [] : props.value;
      const value = Array.isArray(_value) ? _value : [_value];
      const newValue = checked ? value.concat([eventValue]) : value.filter(v => v !== eventValue);
      const newEvent = syntheticEvent(newValue, event.source);
      onChange(newEvent);
    } else {
      const newEvent = syntheticEvent(eventValue, event.source);
      onChange(newEvent);
    }
  };
  const isGroupOption = ('options' in option);
  if (isGroupOption) {
    const _value = props.value === undefined ? [] : props.value;
    const value = Array.isArray(_value) ? _value : [_value];
    if (option.flatLayout) {
      return /*#__PURE__*/_jsx(OptionGroup, Object.assign({}, props, {
        InputComponent: InputComponent,
        onGroupToggle: () => {},
        onChange: handleChange,
        option: option,
        value: value
      }));
    }
    const handleGroupToggle = event => {
      const checked = event.source.target.checked;
      const groupValues = option.options.map(o => o.value);
      const valueMinusGroup = value.filter(v => !groupValues.includes(v));
      const newValue = checked ? valueMinusGroup.concat(groupValues) : valueMinusGroup;
      const newEvent = syntheticEvent(newValue, event.source);
      onChange(newEvent);
    };
    return /*#__PURE__*/_jsx(OptionGroup, Object.assign({}, props, {
      InputComponent: InputComponent,
      onGroupToggle: handleGroupToggle,
      onChange: handleChange,
      option: option,
      value: value
    }));
  } else {
    const value = props.value;
    const checked = Array.isArray(value) ? value.includes(option.value) : value === option.value;
    return /*#__PURE__*/_jsx(OptionItem, Object.assign({}, props, {
      checked: checked,
      InputComponent: InputComponent,
      onChange: handleChange,
      option: option
    }));
  }
}